import React from 'react'
import { Button, Text, LoadingSpinner, SortableTableHeader } from '../../components/ui'
import {
  TriagemDetailModal,
  type TriagemDetailAppointmentHint,
} from '../../components/centralAmparo/TriagemDetailModal'
import { useAuthContext } from '../../contexts/AuthContext'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import { supportCenterAppointmentsService } from '../../services/supportCenterAppointments'
import { supportCenterReportsService } from '../../services/supportCenterReports'
import { supportCenterSpecialtiesService } from '../../services/supportCenterSpecialties'
import { supportCenterTriageService } from '../../services/supportCenterTriage'
import type { SupportCenterSpecialization } from '../../types/supportCenterSpecialties'
import type { SupportCenterAttendantOption } from '../../types/supportCenterAppointments'
import type { SupportCenterHistoryItem } from '../../types/supportCenterReports'
import { APPOINTMENT_STATUS_LABELS } from '../../types/supportCenterReports'
import type {
  SupportCenterAppointmentStatus,
  SupportCenterTriageListItem,
  SupportCenterTriageStatus,
} from '../../types/supportCenterTriage'
import { TRIAGE_STATUS_LABELS } from '../../types/supportCenterTriage'
import { triageColumnBadgeClass } from '../../components/centralAmparo/triagemHelpers'
import {
  DASHBOARD_INPUT_CLASS,
  appointmentStatusBadgeClass,
  daysAgoIso,
  formatCpf,
  formatDateTime,
  formatPhone,
  getErrorMessage,
  patientKinshipLabel,
  todayIso,
} from '../../components/centralAmparo/dashboardHelpers'

const MODULE_NAME = 'central_amparo_calendario'
const ITEMS_PER_PAGE = 20

type AtendimentosSortKey =
  | 'scheduled_at'
  | 'holder_name'
  | 'patient_name'
  | 'attendant_name'
  | 'specialization_name'
  | 'status'
  | 'triage_status'
  | 'contact'

function compareSortValues(a: string, b: string, order: 'asc' | 'desc'): number {
  const cmp = a.localeCompare(b, 'pt-BR', { numeric: true, sensitivity: 'base' })
  return order === 'asc' ? cmp : -cmp
}

function sortValueForRow(row: SupportCenterHistoryItem, key: AtendimentosSortKey): string {
  switch (key) {
    case 'scheduled_at':
      return row.scheduled_at || ''
    case 'holder_name':
      return (row.holder_name || '').trim()
    case 'patient_name':
      return (row.patient_name || '').trim()
    case 'attendant_name':
      return (row.attendant_name || '').trim()
    case 'specialization_name':
      return (row.specialization_name || '').trim()
    case 'status':
      return APPOINTMENT_STATUS_LABELS[row.status] || row.status
    case 'triage_status':
      return row.triage_status ? TRIAGE_STATUS_LABELS[row.triage_status] : 'Sem triagem'
    case 'contact':
      return `${row.patient_phone || ''} ${row.client_email || ''}`.trim()
  }
}

const CalendarioAtendimentosTab: React.FC = () => {
  const { user, profile } = useAuthContext()
  const actorId = user?.id || profile?.id || null
  const { logView } = useUserLogs()
  const { hasPermission: canViewValues, loading: loadingViewValues } = usePermission(
    MODULE_NAME,
    'view_values'
  )
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')

  const [specializations, setSpecializations] = React.useState<SupportCenterSpecialization[]>([])
  const [allowedSpecializationIds, setAllowedSpecializationIds] = React.useState<string[]>([])
  const [scopeReady, setScopeReady] = React.useState(false)
  const [scopeError, setScopeError] = React.useState<string | null>(null)

  const [dateFrom, setDateFrom] = React.useState(() => daysAgoIso(30))
  const [dateTo, setDateTo] = React.useState(() => todayIso())
  const [specializationId, setSpecializationId] = React.useState('')
  const [appointmentStatus, setAppointmentStatus] = React.useState<
    SupportCenterAppointmentStatus | 'all'
  >('all')
  const [triageStatus, setTriageStatus] = React.useState<
    SupportCenterTriageStatus | 'all' | 'none'
  >('all')
  const [attendantEmail, setAttendantEmail] = React.useState('')
  const [patientQuery, setPatientQuery] = React.useState('')
  const [debouncedPatientQuery, setDebouncedPatientQuery] = React.useState('')
  const [attendants, setAttendants] = React.useState<SupportCenterAttendantOption[]>([])
  const [rows, setRows] = React.useState<SupportCenterHistoryItem[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [loaded, setLoaded] = React.useState(false)
  const [sortBy, setSortBy] = React.useState<AtendimentosSortKey>('scheduled_at')
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc')
  const [currentPage, setCurrentPage] = React.useState(1)

  const [detailOpen, setDetailOpen] = React.useState(false)
  const [selectedTriage, setSelectedTriage] = React.useState<SupportCenterTriageListItem | null>(
    null
  )
  const [appointmentHint, setAppointmentHint] =
    React.useState<TriagemDetailAppointmentHint | null>(null)
  const [triageDetailLoading, setTriageDetailLoading] = React.useState(false)

  React.useEffect(() => {
    const handler = window.setTimeout(() => setDebouncedPatientQuery(patientQuery), 350)
    return () => window.clearTimeout(handler)
  }, [patientQuery])

  React.useEffect(() => {
    let cancelled = false
    const loadScope = async () => {
      if (loadingViewValues) return
      if (!actorId) {
        setSpecializations([])
        setAllowedSpecializationIds([])
        setScopeError(null)
        setScopeReady(true)
        return
      }
      setScopeReady(false)
      setScopeError(null)
      try {
        const allSpecs = await supportCenterSpecialtiesService.listSpecializations()
        if (cancelled) return
        // Catálogo completo no filtro; escopo de listagem pelo professional_user_id quando sem ver valores.
        setSpecializations(allSpecs)
        setAllowedSpecializationIds(allSpecs.map((spec) => spec.id))
      } catch (e) {
        if (!cancelled) {
          setSpecializations([])
          setAllowedSpecializationIds([])
          setScopeError(getErrorMessage(e))
        }
      } finally {
        if (!cancelled) setScopeReady(true)
      }
    }
    void loadScope()
    return () => {
      cancelled = true
    }
  }, [actorId, canViewValues, loadingViewValues])

  React.useEffect(() => {
    const allowed = new Set(allowedSpecializationIds)
    setSpecializationId((prev) => (prev && !allowed.has(prev) ? '' : prev))
  }, [allowedSpecializationIds])

  React.useEffect(() => {
    if (loadingViewValues) return
    if (!canViewValues) {
      setAttendants([])
      setAttendantEmail('')
      return
    }
    let cancelled = false
    const loadAttendants = async () => {
      try {
        const list = await supportCenterAppointmentsService.listAttendantsForAgenda(
          specializationId || undefined
        )
        if (cancelled) return
        setAttendants(list)
        setAttendantEmail((prev) =>
          prev && !list.some((attendant) => attendant.email === prev) ? '' : prev
        )
      } catch {
        if (!cancelled) {
          setAttendants([])
          setAttendantEmail('')
        }
      }
    }
    void loadAttendants()
    return () => {
      cancelled = true
    }
  }, [canViewValues, loadingViewValues, specializationId])

  const loadAppointments = React.useCallback(async () => {
    if (!scopeReady || loadingViewValues) return
    setLoading(true)
    setError(null)
    try {
      let professionalUserId: string | undefined
      if (!canViewValues) {
        professionalUserId = actorId || undefined
        if (!professionalUserId) {
          setRows([])
          setLoaded(true)
          setError(
            'Não foi possível identificar o usuário logado para filtrar os atendimentos.'
          )
          return
        }
      } else if (attendantEmail.trim()) {
        const email = attendantEmail.trim().toLowerCase()
        const match = attendants.find((a) => a.email.trim().toLowerCase() === email)
        professionalUserId = match?.id
        if (!professionalUserId) {
          setRows([])
          setLoaded(true)
          return
        }
      }

      const data = await supportCenterReportsService.listAppointments({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        specialization_id: specializationId || undefined,
        specialization_ids: allowedSpecializationIds,
        appointment_statuses: appointmentStatus === 'all' ? undefined : [appointmentStatus],
        triage_statuses: triageStatus === 'all' ? undefined : [triageStatus],
        professional_user_id: professionalUserId,
        patient_query: debouncedPatientQuery || undefined,
      })
      setRows(data)
      setLoaded(true)
    } catch (e) {
      setError(getErrorMessage(e))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [
    scopeReady,
    loadingViewValues,
    canViewValues,
    actorId,
    attendants,
    allowedSpecializationIds,
    dateFrom,
    dateTo,
    specializationId,
    appointmentStatus,
    triageStatus,
    attendantEmail,
    debouncedPatientQuery,
  ])

  React.useEffect(() => {
    void loadAppointments()
  }, [loadAppointments])

  const handleClearFilters = () => {
    setDateFrom(daysAgoIso(30))
    setDateTo(todayIso())
    setSpecializationId('')
    setAppointmentStatus('all')
    setTriageStatus('all')
    if (canViewValues) setAttendantEmail('')
    setPatientQuery('')
    setDebouncedPatientQuery('')
    setCurrentPage(1)
  }

  const toggleSort = (key: string) => {
    const nextKey = key as AtendimentosSortKey
    if (sortBy === nextKey) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(nextKey)
      setSortOrder(nextKey === 'scheduled_at' ? 'desc' : 'asc')
    }
    setCurrentPage(1)
  }

  const sortedRows = React.useMemo(() => {
    return [...rows].sort((a, b) =>
      compareSortValues(sortValueForRow(a, sortBy), sortValueForRow(b, sortBy), sortOrder)
    )
  }, [rows, sortBy, sortOrder])

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / ITEMS_PER_PAGE))
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const paginatedRows = sortedRows.slice(startIndex, endIndex)

  React.useEffect(() => {
    setCurrentPage(1)
  }, [rows])

  React.useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  /** Mesmo fluxo do histórico da agenda/dashboard: abas Triagem + Atendimento. */
  const openRowDetail = async (row: SupportCenterHistoryItem) => {
    setAppointmentHint({
      appointment_id: row.appointment_id,
      specialization_name: row.specialization_name,
      appointment_status: row.status,
      triage_status_label: row.triage_status ? TRIAGE_STATUS_LABELS[row.triage_status] : null,
      scheduled_at: row.scheduled_at,
    })

    if (!row.triage_id) {
      setSelectedTriage(null)
      setDetailOpen(true)
      return
    }

    setTriageDetailLoading(true)
    try {
      const detail = await supportCenterTriageService.getCaseById(row.triage_id)
      setSelectedTriage(detail)
      setDetailOpen(true)
      if (detail) {
        void logView(
          'support_center_triage',
          row.triage_id,
          `Visualizou detalhe na lista de atendimentos (${row.triage_status ?? 'sem status'})`
        )
      } else {
        setError('Triagem não encontrada.')
      }
    } catch (e) {
      setSelectedTriage(null)
      setError(getErrorMessage(e))
      setDetailOpen(true)
    } finally {
      setTriageDetailLoading(false)
    }
  }

  const closeDetail = () => {
    if (triageDetailLoading) return
    setDetailOpen(false)
    setSelectedTriage(null)
    setAppointmentHint(null)
  }

  if (!scopeReady || loadingViewValues) {
    return (
      <div className="py-20 flex justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (scopeError) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
        <Text size="sm">{scopeError}</Text>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Data inicial
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className={DASHBOARD_INPUT_CLASS}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Data final
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className={DASHBOARD_INPUT_CLASS}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Especialidade
            </label>
            <select
              value={specializationId}
              onChange={(e) => {
                setSpecializationId(e.target.value)
                setAttendantEmail('')
              }}
              className={DASHBOARD_INPUT_CLASS}
            >
              <option value="">Todas</option>
              {specializations.map((spec) => (
                <option key={spec.id} value={spec.id}>
                  {spec.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Profissional
            </label>
            {canViewValues ? (
              <select
                value={attendantEmail}
                onChange={(e) => setAttendantEmail(e.target.value)}
                className={DASHBOARD_INPUT_CLASS}
              >
                <option value="">Todos</option>
                {attendants.map((attendant) => (
                  <option key={attendant.email} value={attendant.email}>
                    {attendant.name}
                  </option>
                ))}
              </select>
            ) : (
                <input
                  type="text"
                  value={profile?.name?.trim() || 'Usuário não identificado'}
                  disabled
                  className={`${DASHBOARD_INPUT_CLASS} opacity-70 cursor-not-allowed`}
                  title="Sem permissão de ver valores: filtro travado na sua agenda"
                />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Status do atendimento
            </label>
            <select
              value={appointmentStatus}
              onChange={(e) =>
                setAppointmentStatus(e.target.value as SupportCenterAppointmentStatus | 'all')
              }
              className={DASHBOARD_INPUT_CLASS}
            >
              <option value="all">Todos</option>
              {(Object.keys(APPOINTMENT_STATUS_LABELS) as SupportCenterAppointmentStatus[]).map(
                (status) => (
                  <option key={status} value={status}>
                    {APPOINTMENT_STATUS_LABELS[status]}
                  </option>
                )
              )}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Status da triagem
            </label>
            <select
              value={triageStatus}
              onChange={(e) =>
                setTriageStatus(e.target.value as SupportCenterTriageStatus | 'all' | 'none')
              }
              className={DASHBOARD_INPUT_CLASS}
            >
              <option value="all">Todos</option>
              <option value="none">Sem triagem</option>
              {(Object.keys(TRIAGE_STATUS_LABELS) as SupportCenterTriageStatus[]).map((status) => (
                <option key={status} value={status}>
                  {TRIAGE_STATUS_LABELS[status]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Paciente (nome ou CPF)
            </label>
            <input
              type="text"
              value={patientQuery}
              onChange={(e) => setPatientQuery(e.target.value)}
              placeholder="Nome ou CPF..."
              className={DASHBOARD_INPUT_CLASS}
            />
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              onClick={handleClearFilters}
              disabled={loading}
              className="w-full"
            >
              Limpar filtros
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <Text size="sm">{error}</Text>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <Text size="lg" className="text-gray-900 dark:text-white">
            Lista de atendimentos
          </Text>
          {loaded && (
            <Text size="sm" color="muted">
              {rows.length} {rows.length === 1 ? 'registro' : 'registros'}
            </Text>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <Text size="sm" color="muted">
              Nenhum atendimento encontrado com os filtros selecionados.
            </Text>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <SortableTableHeader
                    sortKey="scheduled_at"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Data
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="holder_name"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Segurado
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="patient_name"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Paciente
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="attendant_name"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Profissional
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="specialization_name"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Especialidade
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="status"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Status
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="triage_status"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Triagem
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="contact"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Contato
                  </SortableTableHeader>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {paginatedRows.map((row) => (
                    <tr
                      key={row.appointment_id}
                      onClick={() => void openRowDetail(row)}
                      className="hover:bg-primary-50 dark:hover:bg-primary-900/20 cursor-pointer"
                      title="Ver detalhe da triagem e do atendimento"
                    >
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                        {formatDateTime(row.scheduled_at)
                          .split(',')
                          .map((part) => part.trim())
                          .filter(Boolean)
                          .map((part, index) => (
                            <div
                              key={`${row.appointment_id}-dt-${index}`}
                              className={
                                index === 0 ? undefined : 'text-xs text-gray-500 dark:text-gray-400'
                              }
                            >
                              {part}
                            </div>
                          ))}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                        <div>{row.holder_name || '?'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {formatCpf(row.holder_cpf)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                        <div>{row.patient_name || '?'}</div>
                        {patientKinshipLabel(row) ? (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {patientKinshipLabel(row)}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                        {row.attendant_name || '?'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                        {row.specialization_name || '?'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${appointmentStatusBadgeClass(
                            row.status
                          )}`}
                        >
                          {APPOINTMENT_STATUS_LABELS[row.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${triageColumnBadgeClass(
                            row.triage_status
                          )}`}
                        >
                          {row.triage_status ? TRIAGE_STATUS_LABELS[row.triage_status] : 'Sem triagem'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                        <div>{formatPhone(row.patient_phone) || '?'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {row.client_email || '?'}
                        </div>
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && rows.length > 0 && (
          <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <Text size="sm" className="text-gray-700 dark:text-gray-300">
                Mostrando {startIndex + 1} a {Math.min(endIndex, sortedRows.length)} de{' '}
                {sortedRows.length} registros
              </Text>
              {totalPages > 1 && (
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Anterior
                  </button>
                  <div className="flex space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                      let pageNumber: number
                      if (totalPages <= 5) {
                        pageNumber = index + 1
                      } else if (currentPage <= 3) {
                        pageNumber = index + 1
                      } else if (currentPage >= totalPages - 2) {
                        pageNumber = totalPages - 4 + index
                      } else {
                        pageNumber = currentPage - 2 + index
                      }
                      return (
                        <button
                          key={pageNumber}
                          type="button"
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            currentPage === pageNumber
                              ? 'bg-primary-600 text-white'
                              : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      )
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Próximo
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {triageDetailLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <LoadingSpinner />
        </div>
      )}

      {detailOpen && (
        <TriagemDetailModal
          triage={selectedTriage}
          appointmentHint={appointmentHint}
          canUpdate={!!canUpdate}
          onClose={closeDetail}
        />
      )}
    </div>
  )
}

export default CalendarioAtendimentosTab
